'use client'

import * as React from 'react'
import * as Select from '@radix-ui/react-select'
import { ChevronDown, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSearchStore } from '@/lib/store'
import { SORT_OPTIONS } from '@/lib/constants'
import type { SortOption } from '@/lib/types'
import { useI18n } from '@/lib/i18n'

interface SortSelectProps {
  className?: string
  onSort?: () => void
}

export function SortSelect({ className, onSort }: SortSelectProps) {
  const { t } = useI18n()
  const { sort, setSort, travelTime } = useSearchStore()
  
  const handleChange = (value: SortOption) => {
    setSort(value)
    onSort?.()
  }
  
  // Filter out distance option if no travel time filter
  const options = SORT_OPTIONS.filter(
    (opt) => opt.value !== 'distance' || travelTime !== null
  )
  
  const selectedOption = SORT_OPTIONS.find((opt) => opt.value === sort)
  
  return (
    <Select.Root value={sort} onValueChange={handleChange}>
      <Select.Trigger
        className={cn(
          'inline-flex items-center justify-between gap-2 rounded-full',
          'px-3.5 py-2 text-[13px] font-semibold',
          'bg-surface border border-outline text-foreground',
          'transition-transform active:scale-[0.97]',
          'focus:outline-none focus:ring-2 focus:ring-ring',
          className
        )}
        title={t('search.changeSort', 'Change sort order')}
      >
        {/* The "Sort:" prefix is decorative — drop it below sm so the
            trigger fits beside the count headline on phones. */}
        <span className="hidden sm:inline text-muted-foreground">{t('search.sortBy', 'Sort:')}</span>
        <Select.Value>
          <span className="font-medium">{selectedOption?.label}</span>
        </Select.Value>
        <Select.Icon>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </Select.Icon>
      </Select.Trigger>
      
      <Select.Portal>
        <Select.Content
          className={cn(
            'overflow-hidden rounded-xl',
            'bg-popover border border-border/40 shadow-lg',
            'animate-in fade-in-0 zoom-in-95',
            'z-50'
          )}
          position="popper"
          sideOffset={4}
        >
          <Select.Viewport className="p-1">
            {options.map((option) => (
              <Select.Item
                key={option.value}
                value={option.value}
                className={cn(
                  'relative flex items-center px-8 py-2 text-sm rounded-md',
                  'cursor-pointer select-none outline-none',
                  'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground'
                )}
              >
                <Select.ItemIndicator className="absolute left-2">
                  <Check className="h-4 w-4" />
                </Select.ItemIndicator>
                <Select.ItemText>{option.label}</Select.ItemText>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  )
}

